/* oxlint-disable typescript/no-explicit-any -- uses type a = any pattern for generic typing */
import MarkdownExit, { loadFlatYaml, parseAttributeBlock, Token } from '@w/md'
import { incrementalBlockId, ulid } from '@w/utils'
import {
  type Block,
  type BookBlock,
  type BookBlocks,
  type BlockListSubtype,
  type Blocks,
  type BlockTD,
  type BlockTR,
  type CommentMessage,
  type CommentThread,
  blockId,
  FormatFlag,
  type PageMeta,
  RULE_SCOPES,
  textToBlockLetters,
  unwrapReviewTableCells,
  unwrapReviewTableRows,
} from './block'
import { validateCommentDocument } from './comments'
import { BLOCK_RULES, isDroppedKey } from './block-schema'
import { parseHtmlTable } from './table-html'
import { cellInline } from './cell-inline'

// INV-2: pre.children plain text never encodes the fence-closing newline; the
// serializer appends it, the parser strips it.
import { blocksToMd } from './serializer'
import type { MarkdownSourceMap, MarkdownSourceRange } from './types'
import { getFractionalIndex } from './utils/get-fractional-index'
import { rulePracticeQid } from './rule-practice'

export interface MdToBlocksOptions {
  profile?: 'editor' | 'book'
  withChars?: boolean
  /**
   * Collapse inline formatting into non-collaborative text blocks.
   * Use for imported/book content that must not create chars blocks.
   */
  plainTextBlocks?: boolean
  fractionalIndexBefore?: string
  fractionalIndexAfter?: string
  nextFractionalIndex?: (existing?: string) => string
  incrementalIds?: boolean
  /** Front matter keys allowed beyond the editor's pageMeta fields (block-schema BLOCK_RULES.pageMeta,
   * content-blocks-editor.spec.md:173), e.g. the Flashcards page columns (flashcards-cli.spec.md:12). */
  pageMetaKeys?: readonly string[]
  /** Render repository frontmatter as YAML code instead of typed editor page fields. */
  frontmatter?: 'pageMeta' | 'code'
}

export interface MarkdownBlockDocument {
  blocks: Blocks
  comments: CommentThread[]
  sourceMap: MarkdownSourceMap
}

type Options = MdToBlocksOptions & {
  nextBlockId?: () => string
}
type ParseOptions = Options & {
  nextBlockId: () => string
  sourceMap?: Map<string, MarkdownSourceRange>
}

type a = any

const FORMAT_SPACE_SENTINEL = '\u{F0000}'

function readCommaSeparatedAttribute(value: unknown): string[] {
  if (typeof value !== 'string') return []
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

/** A rule's or question group's `.practice` clozes and questions are one question: the first one's qid (authored or
 * minted) is every later one's (rulePracticeQid). */
function sharePracticeQid(children: Blocks): Blocks {
  const qid = rulePracticeQid(children)
  if (qid === undefined) return children
  const visit = (items: Blocks): Blocks => items.map((block): Block => {
    if ((block.type === 'cloze' || block.type === 'question') && block.practice) return block.qid === qid ? block : { ...block, qid }
    return 'children' in block && Array.isArray(block.children) ? { ...block, children: visit(block.children as Blocks) } as Block : block
  })
  return visit(children)
}

function readRuleAffix(attributes: Record<string, unknown>): { affix?: Extract<Block, { type: 'rule' }>['affix'] } {
  const { affixKind: kind, affixForm: form, affixMeaning: meaning } = attributes
  if (kind === undefined && form === undefined && meaning === undefined) return {}
  if ((kind !== 'prefix' && kind !== 'suffix') || typeof form !== 'string' || !form.trim() || typeof meaning !== 'string' || !meaning.trim()) {
    throw new Error('rule affix requires prefix/suffix kind, form and meaning')
  }
  if (attributes.scope !== 'derivation' || readCommaSeparatedAttribute(attributes.predicts).length > 0) {
    throw new Error('Affix rules require derivation scope and no predicted inflection cells')
  }
  return { affix: { kind, form, meaning } }
}

export function mdToBlocks(
  md: string,
  options: MdToBlocksOptions & { profile: 'book' },
): BookBlocks
export function mdToBlocks(
  md: string,
  options?: MdToBlocksOptions & { profile?: 'editor' },
): Blocks
export function mdToBlocks(
  md: string,
  options: MdToBlocksOptions,
): Blocks | BookBlocks
export function mdToBlocks(
  md: string,
  options: MdToBlocksOptions = {},
): Blocks | BookBlocks {
  const tokens = parseMarkdownPreservingFormatSpaces(md)
  const parseOptions = createParseOptions(options)
  const blocks = normalizeEditorText(tokensToBlocks(tokens, parseOptions), parseOptions)
  validateCommentDocument(blocks)
  validatePageMeta(blocks, options.pageMetaKeys)
  if (options.profile === 'book') return editorBlocksToBookBlocks(blocks)
  return blocks
}

/** pageMeta is closed: its keys are the spec's (MdToBlocksOptions.pageMetaKeys), so an unknown front matter key
 * rejects with "Invalid pageMeta" before any caller writes the blocks (flashcards-cli.spec.md:12). */
function validatePageMeta(blocks: Blocks, extra: readonly string[] = []) {
  const meta = blocks.find(block => block.type === 'pageMeta')
  if (!meta) return
  const rule = BLOCK_RULES.pageMeta
  const allowed = new Set(['type', ...Object.keys(rule?.required ?? {}), ...Object.keys(rule?.optional ?? {}), ...extra])
  const unknown = Object.keys(meta).filter(key => !allowed.has(key) && !isDroppedKey(key))
  if (unknown.length > 0) throw new Error(`Invalid pageMeta: unknown key ${unknown.join(', ')}`)
}

/**
 * Parses renderable blocks together with their Markdown line ranges when the
 * source token provides one.
 * Source lines are 1-based and inclusive at both ends.
 */
export function mdToBlockDocument(
  md: string,
  options: MdToBlocksOptions & { profile?: 'editor' } = {},
): MarkdownBlockDocument {
  const tokens = parseMarkdownPreservingFormatSpaces(md)
  const sourceMap = new Map<string, MarkdownSourceRange>()
  const parseOptions = { ...createParseOptions(options), sourceMap }
  const blocks = normalizeEditorText(tokensToBlocks(tokens, parseOptions), parseOptions)
  validateCommentDocument(blocks)
  validatePageMeta(blocks, options.pageMetaKeys)
  const registry = blocks.find((block) => block.type === 'comments')
  return {
    blocks: blocks.filter((block) => block.type !== 'comments'),
    comments: registry?.threads ?? [],
    sourceMap,
  }
}

function mdToEditorBlocks(md: string, options: Options): Blocks {
  const tokens = parseMarkdownPreservingFormatSpaces(md)
  return tokensToBlocks(
    tokens,
    createParseOptions({ ...options, profile: 'editor' }),
  )
}

function parseMarkdownPreservingFormatSpaces(md: string, inline = false): Token[] {
  const formatDelimiter = String.raw`(?:\*\*|__|\*|_|~~)`
  const protectedMarkdown = md
    .replaceAll(
      new RegExp(`(${formatDelimiter})((?:&#32;)+)`, 'gu'),
      (_match, delimiter: string, spaces: string) =>
        delimiter + spaces.replaceAll('&#32;', FORMAT_SPACE_SENTINEL),
    )
    .replaceAll(
      new RegExp(`((?:&#32;)+)(?=${formatDelimiter})`, 'gu'),
      (spaces: string) => spaces.replaceAll('&#32;', FORMAT_SPACE_SENTINEL),
    )
  const parser = MarkdownExit()
  parser.block.ruler.before('reference', 'footnote_definition', (state, start, end, silent) => {
    const offset = state.bMarks[start] + state.tShift[start]
    if (state.sCount[start] - state.blkIndent >= 4 || !/^\[\^[^\]\n]+\]:/u.test(state.src.slice(offset))) return false
    if (silent) return true
    let next = start + 1
    while (next < end && (state.sCount[next] - state.blkIndent >= 4 || state.isEmpty(next))) next += 1
    while (next > start + 1 && state.isEmpty(next - 1)) next -= 1
    const token = state.push('footnote_definition', '', 0)
    token.content = state.getLines(start, next, state.blkIndent, false).replace(/\n$/u, '')
    token.map = [start, next]
    state.line = next
    return true
  })
  parser.block.ruler.before('paragraph', 'html_table', (state, start, end, silent) => {
    const offset = state.bMarks[start] + state.tShift[start]
    if (state.sCount[start] - state.blkIndent >= 4 || !/^<table[\s>]/i.test(state.src.slice(offset))) return false
    let depth = 0
    let finish = -1
    for (const match of state.src.slice(offset).matchAll(/<\/?table\b[^>]*>/gi)) {
      depth += match[0].startsWith('</') ? -1 : 1
      if (depth === 0) { finish = offset + match.index + match[0].length; break }
    }
    if (finish < 0) throw new Error('Unclosed HTML table')
    if (silent) return true
    let next = start + 1
    while (next < end && state.bMarks[next] < finish) next++
    const token = state.push('html_table', '', 0)
    token.content = state.src.slice(offset, finish)
    token.map = [start, next]
    state.line = next
    return true
  }, { alt: ['paragraph'] })
  const tokens = inline ? parser.parseInline(protectedMarkdown, {}) : parser.parse(protectedMarkdown, {})

  const restore = (token: Token): void => {
    // CriticMarkup content is Markdown read again later: its protected spaces go back to the entity they were.
    const raw = token.type === 'diff_inline' || token.type === 'critic_comment'
    const space = raw ? '&#32;' : ' '
    token.content = token.content.replaceAll(FORMAT_SPACE_SENTINEL, space)
    if (raw && token.meta) {
      for (const key of ['old', 'new'] as const) {
        if (typeof token.meta[key] === 'string') token.meta[key] = token.meta[key].replaceAll(FORMAT_SPACE_SENTINEL, space)
      }
    }
    token.children?.forEach(restore)
  }
  tokens.forEach((token) => {
    if (token.type === 'reference' && token.map) token.content = protectedMarkdown.split('\n').slice(token.map[0], token.map[1]).join('\n')
    restore(token)
  })
  return tokens
}

function mdToInlineEditorBlocks(md: string, options: Options): Blocks {
  const parsed = mdToEditorBlocks(md, options)
  if (parsed.length === 1 && parsed[0]?.type === 'p') {
    return parsed[0].children
  }
  return parsed
}


/**
 * Lexical's markdown import starts a new list when a bullet line's kind changes (`- item` vs `- [ ] task`),
 * so plain items never become checkboxes. GFM keeps one list; the serializer's alternating markers keep the
 * split lists apart on the way back.
 */
function splitTaskListRuns(tokens: Token[]): Token[] {
  const result: Token[] = []
  const lists: { open: Token; task?: boolean }[] = []
  for (const token of tokens) {
    if (token.type === 'bullet_list_open' || token.type === 'ordered_list_open') lists.push({ open: token })
    else if (token.type === 'bullet_list_close' || token.type === 'ordered_list_close') lists.pop()
    else if (token.type === 'list_item_open') {
      const list = lists.at(-1)
      const task = !!token.meta?.task
      if (list?.open.type === 'bullet_list_open' && list.task !== undefined && list.task !== task) {
        const close = new Token('bullet_list_close', list.open.tag, -1)
        const open = new Token('bullet_list_open', list.open.tag, 1)
        for (const split of [close, open]) Object.assign(split, { markup: list.open.markup, level: list.open.level, block: true })
        if (token.map && list.open.map) open.map = [token.map[0], list.open.map[1]]
        result.push(close, open)
        list.open = open
      }
      if (list) list.task = task
    }
    result.push(token)
  }
  return result
}

function tokensToBlocks(allTokens: Token[], options: ParseOptions): Blocks {
  const tokens = splitTaskListRuns(allTokens)
  const parseOptions = options.nextFractionalIndex
    ? options
    : {
        ...options,
        nextFractionalIndex: createFractionalIndexGenerator(
          options,
          collectExistingFractionalIndexes(tokens),
        ),
      }
  const blocks: Blocks = []
  let i = 0

  while (i < tokens.length) {
    const [block, nextIndex] = tokenToBlock(tokens, i, parseOptions)
    if (block) {
      blocks.push(block)
    }
    i = nextIndex
  }

  return blocks
}

function markdownSourceRange(token: Token): MarkdownSourceRange | undefined {
  if (!Array.isArray(token.map)) return undefined
  const [startLine, endLineExclusive] = token.map
  if (endLineExclusive <= startLine) return undefined
  return {
    startLine: startLine + 1,
    endLine: endLineExclusive,
  }
}

function createParseOptions(options: Options): ParseOptions {
  const bookProfile = options.profile === 'book'
  return {
    ...options,
    withChars: bookProfile || options.plainTextBlocks ? false : options.withChars ?? true,
    plainTextBlocks: bookProfile ? true : options.plainTextBlocks,
    nextBlockId:
      options.nextBlockId ??
      (bookProfile || options.incrementalIds
        ? incrementalBlockId()
        : uniqueBlockId()),
  }
}

// blockId() is only 4 chars (63^4 ≈ 15.7M values) — a ~300-block document has
// real birthday-collision odds (~1/400), so ids must be unique within a parse.
function uniqueBlockId() {
  const seen = new Set<string>()
  return () => {
    let id = blockId()
    while (seen.has(id)) id = blockId()
    seen.add(id)
    return id
  }
}

function editorBlocksToBookBlocks(blocks: Blocks): BookBlocks {
  const result: BookBlocks = []
  for (const block of blocks) {
    if (block.type === 'l') {
      result.push(...editorInlineBlocksToBookInlineBlocks(block.children))
      continue
    }
    const converted = editorBlockToBookBlock(block)
    if (converted) result.push(converted)
  }
  return result
}

function editorBlockToBookBlock(block: Block): BookBlock | undefined {
  // oxlint-disable-next-line typescript-eslint/switch-exhaustiveness-check -- book profile intentionally keeps only book-safe block types.
  switch (block.type) {
    case 'text':
      return {
        id: block.id,
        type: 'text',
        text: block.text,
        ...(block.escaped ? { escaped: block.escaped } : {}),
      }
    case 'br':
      return { id: block.id, type: 'br' }
    case 'code':
      return {
        id: block.id,
        type: 'code',
        latex: block.latex,
        children: editorInlineBlocksToBookInlineBlocks(block.children),
      }
    case 'em':
      return {
        id: block.id,
        type: 'em',
        color: block.color,
        children: editorInlineBlocksToBookInlineBlocks(block.children),
      }
    case 'a':
      return {
        id: block.id,
        type: 'a',
        href: block.href,
        children: editorInlineBlocksToBookInlineBlocks(block.children),
      }
    case 'h':
      return {
        id: block.id,
        type: 'h',
        level: block.level,
        children: editorInlineBlocksToBookInlineBlocks(block.children),
      }
    case 'quote':
      return {
        id: block.id,
        type: 'quote',
        children: editorInlineBlocksToBookInlineBlocks(block.children),
      }
    case 'p':
      return {
        id: block.id,
        type: 'p',
        children: editorInlineBlocksToBookInlineBlocks(block.children),
      }
    case 'list':
      return {
        id: block.id,
        type: 'list',
        subtype: block.subtype === 'ordered' ? 'ordered' : undefined,
        spread: block.spread,
        start: block.start,
        children: block.children
          .map((child) =>
            child.type === 'li' ? editorBlockToBookBlock(child) : undefined,
          )
          .filter(
            (child): child is Extract<BookBlock, { type: 'li' }> =>
              child !== undefined,
          ),
      }
    case 'li':
      return {
        id: block.id,
        type: 'li',
        done: block.done,
        children: editorInlineBlocksToBookInlineBlocks(block.children),
      }
    case 'table':
      return {
        id: block.id,
        type: 'table',
        children: unwrapReviewTableRows(block.children).map((row) => ({
          id: row.id,
          type: 'tr',
          children: unwrapReviewTableCells(row.children).map((cell) => ({
            id: cell.id,
            type: 'td',
            children: editorInlineBlocksToBookInlineBlocks(cell.children),
          })),
        })),
      }
    case 'pre':
      return {
        id: block.id,
        type: 'pre',
        lang: block.lang,
        ...(block.caption && {
          caption: editorInlineBlocksToBookInlineBlocks(block.caption),
        }),
        children: editorInlineBlocksToBookInlineBlocks(block.children),
      }
    case 'hr':
      return { id: block.id, type: 'sceneBreak' }
    case 'img':
      return {
        id: block.id,
        type: 'img',
        src: block.src,
        alt: block.alt,
        width: block.width,
        caption: block.caption
          ? editorInlineBlocksToBookInlineBlocks(block.caption)
          : undefined,
        children: block.children
          ? editorInlineBlocksToBookInlineBlocks(block.children)
          : undefined,
      }
    default:
      return undefined
  }
}

function editorInlineBlocksToBookInlineBlocks(
  blocks: Blocks,
): Extract<BookBlock, { type: 'text' | 'br' | 'code' | 'em' | 'a' }>[] {
  const result: Extract<
    BookBlock,
    { type: 'text' | 'br' | 'code' | 'em' | 'a' }
  >[] = []
  for (const block of blocks) {
    if (block.type === 'l') {
      result.push(...editorInlineBlocksToBookInlineBlocks(block.children))
      continue
    }
    const converted = editorBlockToBookBlock(block)
    if (
      converted?.type === 'text' ||
      converted?.type === 'br' ||
      converted?.type === 'code' ||
      converted?.type === 'em' ||
      converted?.type === 'a'
    ) {
      result.push(converted)
    }
  }
  return result
}

function createFractionalIndexGenerator(
  options: Options,
  existingFractionalIndexes: string[],
): (existing?: string) => string {
  let before = options.fractionalIndexBefore ?? ''
  const future = [...existingFractionalIndexes]
  const afterBound = options.fractionalIndexAfter ?? ''

  return (existing?: string) => {
    if (existing) {
      const futureIndex = future.indexOf(existing)
      if (futureIndex >= 0) future.splice(futureIndex, 1)
      before = existing
      return existing
    }

    const futureAfter = future.find((index) => !before || index > before)
    const after =
      futureAfter && (!afterBound || futureAfter < afterBound)
        ? futureAfter
        : afterBound
    const next = getFractionalIndex(before, after)
    before = next
    return next
  }
}

function collectExistingFractionalIndexes(tokens: Token[]): string[] {
  const indexes: string[] = []

  for (const token of tokens) {
    if (token.children) {
      indexes.push(...collectExistingFractionalIndexes(token.children))
    }

    const name = token.meta?.name
    if (!isQuestionMetadataDirective(name)) continue

    const attrs = token.meta?.attrs ?? token.meta?.attributes ?? {}
    if (attrs.fractionalIndex) indexes.push(attrs.fractionalIndex)
  }

  return indexes
}

function isQuestionMetadataDirective(name: unknown): boolean {
  return (
    name === 'cloze' ||
    name === 'sa' ||
    name === 'la' ||
    name === 'sc' ||
    name === 'mc' ||
    name === 'sr'
  )
}

function readFractionalIndex(
  attrs: Record<string, any>,
  options: Options,
): string {
  return options.nextFractionalIndex?.(attrs.fractionalIndex) ?? 'A'
}

/**
 * A width attribute as the number block.ts declares: a percentage of the page or the columns block, as the viewer
 * (block-viewer.tsx normalizeWidth) and the editor (core/columns.ts columnPercent) render a number. `50` and `"50%"` read
 * as 50; a value with no percentage (`auto`, `300px`) reads as undefined, the block's default width.
 */
function readPercentWidth(value: unknown): number | undefined {
  const match = typeof value === 'string' ? /^\s*(\d+(?:\.\d+)?)\s*%?\s*$/u.exec(value) : null
  return match ? Number(match[1]) : undefined
}

/** `{ width }` for a media block's optional width, `{}` when the attribute has no percentage. */
function widthField(value: unknown): { width?: number } {
  const width = readPercentWidth(value)
  return width === undefined ? {} : { width }
}

/** A column's required width when the attribute has none: the width the editor draws it at (structure.tsx:129). */
const DEFAULT_COLUMN_WIDTH = 50

function readBlockId(
  attrs: Record<string, any>,
  options: ParseOptions,
): string {
  return typeof attrs.id === 'string' && attrs.id.trim()
    ? attrs.id
    : options.nextBlockId()
}

// Fence metadata holds captions and line annotations; other trailing attributes stay in lang verbatim.
function readFenceInfo(
  info: string,
  options: ParseOptions,
): { lang?: string; caption?: Blocks; lines?: string[] } {
  const start = info.indexOf('{')
  if (start !== -1) {
    const block = parseAttributeBlock(info, start, info.length)
    const keys = Object.keys(block.attributes)
    const lines = readFenceLines(block.attributes.lines)
    if (
      block.ok &&
      info.slice(block.pos).trim() === '' &&
      keys.every(key => key === 'caption' || key === 'captionInline' || key === 'lines') &&
      (block.attributes.caption === undefined || typeof block.attributes.caption === 'string') &&
      (block.attributes.lines === undefined || lines !== undefined) &&
      (typeof block.attributes.caption === 'string' || lines !== undefined)
    ) {
      return {
        lang: info.slice(0, start).trim() || undefined,
        ...(typeof block.attributes.caption === 'string' ? {
          caption: block.attributes.captionInline ? mdToInlineEditorBlocks(block.attributes.caption, options) : mdToEditorBlocks(block.attributes.caption, options),
        } : {}),
        ...(lines === undefined ? {} : { lines }),
      }
    }
  }
  return { lang: info || undefined }
}

function readFenceLines(value: unknown): string[] | undefined {
  if (typeof value !== 'string') return undefined
  try {
    const lines: unknown = JSON.parse(value)
    return Array.isArray(lines) && lines.every((line): line is string => typeof line === 'string') ? lines : undefined
  } catch {
    // An invalid metadata suffix remains part of the language, as other unknown attributes do.
    return undefined
  }
}

function readSpokenResponseAnswer(attrs: Record<string, any>) {
  const src = attrs.answerSrc
  const transcript = attrs.answerTranscript
  // Both are written for every sr question; a new one (not yet recorded) has them empty.
  if (typeof src !== 'string' || typeof transcript !== 'string') {
    throw new TypeError('sr requires answerSrc and answerTranscript')
  }
  return { src, transcript }
}

function applySlotId(id: string, children: Blocks): Blocks {
  if (children[0]) children[0].id = id
  return children
}

function slotTokenToBlocks(tokens: Token[], index: number, options: ParseOptions): [Blocks, number] {
  const token = tokens[index]
  if (token.type === 'directive_block_open' && token.meta?.name === 'inline') {
    const children: Blocks = []
    let next = index + 1
    while (next < tokens.length && tokens[next].type !== 'directive_block_close') {
      const [block, after] = tokenToBlock(tokens, next, options)
      if (block) children.push(...(block.type === 'p' ? block.children : [block]))
      next = after
    }
    return [children, next + 1]
  }
  const [block, next] = tokenToBlock(tokens, index, options)
  return [block ? [block] : [], next]
}

function tokenToBlock(
  tokens: Token[],
  index: number,
  options: ParseOptions,
): [Block | null, number] {
  const result = tokenToBlockValue(tokens, index, options)
  const block = result[0]
  const sourceRange = block ? markdownSourceRange(tokens[index]) : undefined
  if (block && sourceRange) {
    const assign = (child: Block, inherited: MarkdownSourceRange) => {
      const range = options.sourceMap?.get(child.id) ?? inherited
      options.sourceMap?.set(child.id, range)
      if ('children' in child) child.children?.forEach(nested => { assign(nested, range) })
    }
    assign(block, sourceRange)
  }
  return result
}

/** Answer containers are syntax owned by a question, never standalone page blocks. */
function answerDirectiveToBlock(tokens: Token[], index: number, options: ParseOptions): [Block, number] {
  const token = tokens[index]
  const children: Blocks = []
  let next = index + 1
  while (next < tokens.length && tokens[next].type !== 'directive_block_close') {
    const [block, nextIndex] = tokenToBlock(tokens, next, options)
    if (block) children.push(block)
    next = nextIndex
  }
  const content = token.meta?.content
  return [{
    id: readBlockId(token.meta?.attributes ?? {}, options),
    type: 'answer',
    children: content ? ((mdToEditorBlocks(content, options)[0] as a)?.children ?? []) : children,
  } as a, next + 1]
}

function tokenToBlockValue(
  tokens: Token[],
  index: number,
  options: ParseOptions,
): [Block | null, number] {
  const token = tokens[index]

  switch (token.type) {
    case 'reference':
    case 'footnote_definition':
      return [{
        id: options.nextBlockId(),
        type: 'definition',
        kind: token.type === 'reference' ? 'link' : 'footnote',
        children: [{ id: options.nextBlockId(), type: 'text', text: token.content }],
      }, index + 1]
    case 'html_table':
      return [parseHtmlTable(token.content, (md, inline) => {
        if (inline) {
          const parsed = parseMarkdownPreservingFormatSpaces(md, true)
          return inlineTokensToBlocks(parsed[0]?.children ?? [], options)
        }
        return tokensToBlocks(parseMarkdownPreservingFormatSpaces(md), options)
      }, options.nextBlockId), index + 1]

    case 'heading_open': {
      const level = Math.trunc(Number(token.tag.slice(1))) as 1 | 2 | 3
      const inlineToken = tokens[index + 1]
      const children = inlineToken?.children
        ? inlineTokensToBlocks(inlineToken.children, options)
        : []
      return [
        {
          id: options.nextBlockId(),
          type: 'h',
          level: Math.min(3, level) as 1 | 2 | 3,
          children,
        },
        index + 3, // skip _open, inline, _close
      ]
    }

    case 'paragraph_open': {
      const inlineToken = tokens[index + 1]
      const children = inlineToken?.children
        ? inlineTokensToBlocks(inlineToken.children, options)
        : []

      // A paragraph holding only a media or page atom is that block-level atom (the serializer writes them alone)
      if (children.length === 1) {
        const child = children[0]
        if (['img', 'preview', 'video', 'audio', 'file', 'page'].includes(child.type)) {
          return [child, index + 3]
        }
        // Lift inline questions to block level
        if (child.type === 'question' && (child as any).inline) {
          return [child, index + 3]
        }
      }

      return [
        {
          id: options.nextBlockId(),
          type: 'p',
          children,
        },
        index + 3,
      ]
    }

    case 'bullet_list_open':
    case 'ordered_list_open': {
      const listChildren: Blocks = []
      let j = index + 1
      let subtype: BlockListSubtype | undefined

      if (token.type === 'ordered_list_open') {
        subtype = 'ordered'
      }

      while (
        j < tokens.length &&
        tokens[j].type !== 'bullet_list_close' &&
        tokens[j].type !== 'ordered_list_close'
      ) {
        if (tokens[j].type === 'list_item_open') {
          const [liBlock, nextJ] = tokenToBlock(tokens, j, options)
          if (liBlock) {
            listChildren.push(liBlock)
            // Check if it's a todo item
            if ((liBlock as a).done !== undefined) {
              subtype = 'todo'
            }
          }
          j = nextJ
        } else {
          j++
        }
      }

      return [
        {
          id: options.nextBlockId(),
          type: 'list',
          ...(subtype === undefined ? {} : { subtype }),
          ...(token.attrGet('start') ? { start: Number(token.attrGet('start')) } : {}),
          ...(listChildren.length > 1 && hasBlankBetweenChildren(tokens, index, j)
            ? { spread: true } : {}),
          children: listChildren,
        },
        j + 1,
      ]
    }

    case 'list_item_open': {
      const liChildren: Blocks = []
      let j = index + 1
      let done: boolean | undefined

      // Check for checkbox in meta (task list)
      if (token.meta?.task) {
        done = token.meta.taskChecked
      }

      while (j < tokens.length && tokens[j].type !== 'list_item_close') {
        const [block, nextJ] = tokenToBlock(tokens, j, options)
        if (block) {
          liChildren.push(block)
        }
        j = nextJ
      }

      return [
        {
          id: options.nextBlockId(),
          type: 'li',
          ...(done === undefined ? {} : { done }),
          children: liChildren,
        },
        j + 1,
      ]
    }

    case 'hr': {
      return [{ id: options.nextBlockId(), type: 'hr' }, index + 1]
    }

    case 'fence':
    case 'code_block': {
      const content = token.content.endsWith('\n')
        ? token.content.slice(0, -1)
        : token.content
      const { lang, caption, lines: annotations } = readFenceInfo(token.info, options)
      if (lang?.trim().toLowerCase() === 'diff') {
        return [
          {
            id: options.nextBlockId(),
            type: 'pre',
            lang,
            children: diffCodeLinesToBlocks(content, options),
            ...(annotations === undefined ? {} : { lines: annotations }),
            ...(caption && { caption }),
          },
          index + 1,
        ]
      }
      const lines = content.split('\n')
      const children: Blocks = []

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]

        // Code blocks should preserve content as-is without parsing
        if (line || (content === '' && token.content !== '')) {
          children.push({ id: options.nextBlockId(), type: 'text', text: line })
        }

        // Preserve body newlines; fence-closing newline is excluded above.
        if (i < lines.length - 1) {
          children.push({ id: options.nextBlockId(), type: 'text', text: '\n' })
        }
      }

      return [
        {
          id: options.nextBlockId(),
          type: 'pre',
          ...(lang === undefined ? {} : { lang }),
          ...(annotations === undefined ? {} : { lines: annotations }),
          children,
          ...(caption && { caption }),
        },
        index + 1,
      ]
    }

    case 'table_open': {
      const tableChildren: BlockTR[] = []
      let j = index + 1

      // Store table metadata for round-trip preservation
      const tableMeta = token.meta ?? {}

      while (j < tokens.length && tokens[j].type !== 'table_close') {
        if (tokens[j].type === 'tr_open') {
          const [trBlock, nextJ] = tokenToBlock(tokens, j, options)
          if (trBlock) {
            tableChildren.push(trBlock as BlockTR)
          }
          j = nextJ
        } else {
          j++
        }
      }

      return [
        {
          id: options.nextBlockId(),
          type: 'table',
          children: tableChildren,
          ...(tableMeta.aligns && { _aligns: tableMeta.aligns }),
          ...(tableMeta.colWidths && { _colWidths: tableMeta.colWidths }),
        },
        j + 1,
      ]
    }

    case 'tr_open': {
      const trChildren: BlockTD[] = []
      let j = index + 1

      while (j < tokens.length && tokens[j].type !== 'tr_close') {
        if (tokens[j].type === 'th_open' || tokens[j].type === 'td_open') {
          const [tdBlock, nextJ] = tokenToBlock(tokens, j, options)
          if (tdBlock) {
            trChildren.push(tdBlock as BlockTD)
          }
          j = nextJ
        } else {
          j++
        }
      }

      return [
        {
          id: options.nextBlockId(),
          type: 'tr',
          children: trChildren,
        },
        j + 1,
      ]
    }

    case 'th_open':
    case 'td_open': {
      const inlineToken = tokens[index + 1]
      // A cell is an inline-level slot: block-level content reads as its inline content.
      const children = inlineToken?.children
        ? cellInline(inlineTokensToBlocks(inlineToken.children, options), options.nextBlockId)
        : []

      const isHeader = token.type === 'th_open'

      return [
        {
          id: options.nextBlockId(),
          type: 'td',
          children,
          ...(isHeader && { _isHeader: true }),
        } as any,
        index + 3,
      ]
    }

    case 'blockquote_open': {
      const quoteChildren: Blocks = []
      let j = index + 1

      while (j < tokens.length && tokens[j].type !== 'blockquote_close') {
        const [block, nextJ] = tokenToBlock(tokens, j, options)
        if (block) {
          quoteChildren.push(block)
        }
        j = nextJ
      }

      return [
        {
          id: options.nextBlockId(),
          type: 'quote',
          children: quoteChildren,
        },
        j + 1,
      ]
    }

    case 'directive_block_open': {
      const name = token.meta?.name
      const attributes = token.meta?.attributes ?? {}

      let question: Blocks | undefined
      let blockExplanation: Blocks | undefined
      const directiveChildren: Blocks = []
      let j = index + 1

      while (j < tokens.length && tokens[j].type !== 'directive_block_close') {
        const childToken = tokens[j]
        if (['sa', 'la', 'sc', 'mc', 'sr'].includes(name) && childToken.type === 'directive_block_open' && childToken.meta?.name === 'question') {
          const slot: Blocks = []
          let next = j + 1
          while (next < tokens.length && tokens[next].type !== 'directive_block_close') {
            const [blocks, after] = slotTokenToBlocks(tokens, next, options)
            slot.push(...blocks)
            next = after
          }
          const content = childToken.meta?.content
          const parsed = content ? mdToEditorBlocks(content, options) : undefined
          const first = parsed?.[0]
          const blocks = content ? (first && 'children' in first ? first.children ?? [] : []) : slot
          question = applySlotId(readBlockId(childToken.meta?.attributes ?? {}, options), blocks)
          j = next + 1
          continue
        }
        if (['sa', 'la', 'sc', 'mc', 'sr'].includes(name) && childToken.type === 'directive_block_open' && childToken.meta?.name === 'explanation' && childToken.nesting === 1) {
          const slot: Blocks = []
          let next = j + 1
          while (next < tokens.length && tokens[next].type !== 'directive_block_close') {
            const [blocks, after] = slotTokenToBlocks(tokens, next, options)
            slot.push(...blocks)
            next = after
          }
          blockExplanation = applySlotId(readBlockId(childToken.meta?.attributes ?? {}, options), slot)
          j = next + 1
          continue
        }
        const [block, nextJ] =
          ['sa', 'la', 'sc', 'mc', 'sr'].includes(name) &&
          childToken.type === 'directive_block_open' && childToken.meta?.name === 'answer'
            ? answerDirectiveToBlock(tokens, j, options)
            : tokenToBlock(tokens, j, options)
        if (block) {
          directiveChildren.push(block)
        }
        j = nextJ
      }

      // Lift opt/answer/explanation from paragraphs
      const liftedChildren: Blocks = []
      for (const child of directiveChildren) {
        if (child.type === 'p') {
          // Check if paragraph contains only opt/answer/explanation blocks (and whitespace text)
          const allSpecial = child.children.every(
            (c) =>
              (c as a).type === 'opt' ||
              (c as a).type === 'answer' ||
              (c as a).type === 'explanation' ||
              (c.type === 'text' && (c as a).text.trim() === ''),
          )
          if (allSpecial && child.children.length > 0) {
            // Lift all special blocks out of the paragraph (except whitespace text)
            for (const c of child.children) {
              if (c.type !== 'text' || (c as a).text.trim() !== '') {
                liftedChildren.push(c)
              }
            }
            continue
          }
        }
        liftedChildren.push(child)
      }

      // Map directive names to block types
      switch (name) {
        case 'pageLink': return [{ id: options.nextBlockId(), type: 'pageLink', pageId: attributes.pageId, ...(attributes.spaceId === undefined ? {} : { spaceId: attributes.spaceId }) }, j + 1]
        case 'pages': return [{ id: options.nextBlockId(), type: 'pages', ids: attributes.ids ? attributes.ids.split(',') : [] }, j + 1]
        case 'definition': return [{ id: options.nextBlockId(), type: 'definition', kind: attributes.kind, children: liftedChildren.flatMap(child => child.type === 'p' ? child.children : [child]) }, j + 1]
        case 'paragraph': {
          const align: unknown = attributes.align
          if (align !== 'left' && align !== 'center' && align !== 'right') throw new Error('Invalid paragraph alignment')
          const child = liftedChildren[0]
          const children = liftedChildren.length === 1 && child?.type === 'p' ? child.children : liftedChildren
          return [{ id: readBlockId(attributes, options), type: 'p', align, children }, j + 1]
        }
        case 'blockquote': {
          // Parse author attribute as inline markdown
          const author = attributes.author
          const authorBlocks = author
            ? ((mdToEditorBlocks(author, options)[0] as any)?.children ?? [])
            : []
          return [
            {
              id: readBlockId(attributes, options),
              type: 'quote',
              author: authorBlocks,
              children: liftedChildren,
            },
            j + 1,
          ]
        }
        case 'alert': {
          const icon = attributes.icon ?? 'info'
          const color = attributes.color ?? 'info'
          const title = attributes.title
          return [
            {
              id: readBlockId(attributes, options),
              type: 'alert',
              icon,
              color,
              title,
              collapsable: readOptionalDirectiveFlag(attributes, 'collapsable'),
              children: liftedChildren,
            },
            j + 1,
          ]
        }
        case 'review':
          return [
            reviewFromDirective(attributes, liftedChildren, options),
            j + 1,
          ]
        case 'comments':
          return [
            {
              id: readBlockId(attributes, options),
              type: 'comments',
              threads: parseCommentThreads(
                liftedChildren,
                token.meta?.rawContent,
              ),
            },
            j + 1,
          ]
        case 'question-group':
          return [{ id: readBlockId(attributes, options), type: 'questionGroup', children: sharePracticeQid(liftedChildren) }, j + 1]
        case 'rule': {
          const { key, language, scope } = attributes
          if (
            typeof key !== 'string' ||
            key.trim() === '' ||
            typeof language !== 'string' ||
            language.trim() === '' ||
            typeof scope !== 'string' ||
            !RULE_SCOPES.includes(scope as (typeof RULE_SCOPES)[number])
          ) {
            throw new Error('rule requires key, language and scope')
          }
          return [
            {
              id: readBlockId(attributes, options),
              type: 'rule',
              key,
              language,
              scope: scope as (typeof RULE_SCOPES)[number],
              predicts: readCommaSeparatedAttribute(attributes.predicts),
              ...readRuleAffix(attributes),
              ...(attributes.qid ? { qid: attributes.qid } : {}),
              children: sharePracticeQid(liftedChildren),
            },
            j + 1,
          ]
        }

        case 'cols': {
          return [
            {
              id: readBlockId(attributes, options),
              type: 'cols',
              children: liftedChildren,
            },
            j + 1,
          ]
        }

        case 'col': {
          return [
            {
              id: readBlockId(attributes, options),
              type: 'col',
              width: readPercentWidth(attributes.width) ?? DEFAULT_COLUMN_WIDTH,
              children: liftedChildren,
            },
            j + 1,
          ]
        }

        case 'question': {
          // Prompt containers belong to their enclosing question, never page blocks.
          return [null, j + 1]
        }
        case 'la':
        case 'sa':
        case 'sc':
        case 'mc':
        case 'sr': {
          // Question with subtype (la, sa, sc, mc, sr)
          const subtype = name as 'la' | 'sa' | 'sc' | 'mc' | 'sr'
          const qid = attributes.qid ?? (options.incrementalIds ? options.nextBlockId() : ulid())
          const fractionalIndex = readFractionalIndex(attributes, options)

          // Extract question, explanation, opts from directiveChildren
          // oxlint-disable-next-line prefer-const -- assigned conditionally later
          let explanation: Blocks | undefined
          let opts: Blocks[] | undefined
          let answer:
            | string[]
            | number
            | number[]
            | { src: string; transcript: string }
            | undefined

          // Helper to find explanation blocks recursively
          const findExplanation = (blocks: Blocks): Blocks | undefined => {
            for (const block of blocks) {
              if ((block as a).type === 'explanation') {
                return applySlotId(block.id, (block as a).children)
              }
              if ('children' in block && Array.isArray(block.children)) {
                const found = findExplanation(block.children)
                if (found) return found
              }
            }
            return undefined
          }

          // Helper to collect answer blocks recursively. sa accepts multiple
          // ::answer lines; the grader matches the typed answer against any.
          const findAnswers = (blocks: Blocks): string[] => {
            const found: string[] = []
            for (const block of blocks) {
              if ((block as a).type === 'answer') {
                const children = (block as a).children ?? []
                found.push(...shortAnswerText(children))
              } else if ('children' in block && Array.isArray(block.children)) {
                found.push(...findAnswers(block.children))
              }
            }
            return found
          }

          // Find inner :::question[...] and ::opt[...] directives
          const optsList: Blocks[] = []
          const correctIndices: number[] = []

          for (const child of liftedChildren) {
            if ((child as a).type === 'opt') {
              // Extract opt content
              const optContent = applySlotId(
                child.id,
                (child as a).children ?? [],
              )
              optsList.push(optContent)

              // Check if this opt is marked as correct
              const isCorrect = (child as a).correct === true
              if (isCorrect) {
                correctIndices.push(optsList.length - 1)
              }
            }
          }

          if (optsList.length > 0) {
            opts = optsList
            if (subtype === 'sc') {
              answer = correctIndices[0] ?? 0
            } else if (subtype === 'mc') {
              answer = correctIndices
            }
          }

          // Find explanation anywhere in the tree (could be inline)
          explanation = blockExplanation ?? findExplanation(liftedChildren)

          // Find answers for sa questions (one or more ::answer[...])
          if (subtype === 'sa' && !answer) {
            const foundAnswers = findAnswers(liftedChildren)
            if (foundAnswers.length > 0) {
              answer = foundAnswers
            }
          }

          if (subtype === 'sr') answer = readSpokenResponseAnswer(attributes)

          const questionBlock: any = {
            id: readBlockId(attributes, options),
            type: 'question',
            subtype,
            qid,
            fractionalIndex,
          }

          if (subtype === 'la') {
            questionBlock.criteria = readCriteria(attributes.criteria)
          }
          addPromptMetadata(questionBlock, attributes)
          if (subtype === 'sa' || subtype === 'sc' || subtype === 'mc') {
            const inline = readOptionalDirectiveFlag(attributes, 'inline')
            if (inline !== undefined) questionBlock.inline = inline
          }
          if (subtype === 'sa') {
            const strict = readOptionalDirectiveFlag(attributes, 'strict')
            if (strict !== undefined) questionBlock.strict = strict
            const answerRegexp = attributes.answerRegexp ?? attributes.regexp
            if (answerRegexp) questionBlock.answerRegexp = answerRegexp
          }
          if (question) questionBlock.question = question
          if (explanation) questionBlock.explanation = explanation
          if (subtype === 'sa' && answer === undefined) answer = []
          if (subtype === 'sc' || subtype === 'mc') {
            opts ??= []
            answer ??= subtype === 'sc' ? 0 : []
          }
          if (opts) questionBlock.opts = opts
          if (answer !== undefined) questionBlock.answer = answer

          return [questionBlock, j + 1]
        }
        case 'list':
        case 'toggle': {
          // The directive carries list metadata that Markdown cannot infer.
          const list = liftedChildren.find((b) => b.type === 'list')
          if (list) {
            const spread = readOptionalDirectiveFlag(attributes, 'spread')
            return [
              {
                ...list,
                ...(name === 'toggle' ? { subtype: 'toggle' } : {}),
                ...(spread === undefined ? {} : { spread }),
              } as any,
              j + 1,
            ]
          }
          return [null, j + 1]
        }
        default:
          // Skip unknown directives
          return [null, j + 1]
      }
    }

    case 'front_matter': {
      if (options.frontmatter === 'code') {
        return [{
          id: options.nextBlockId(),
          type: 'pre',
          lang: 'yaml',
          children: [{ id: options.nextBlockId(), type: 'text', text: token.content.replace(/\n$/u, '') }],
        }, index + 1]
      }
      if (token.meta?.type === 'yaml') {
        // js-yaml parsed it (md front_matter rule), else the flat reader; front matter neither can read is rejected,
        // as js-yaml's load threw before (CLI import rejects malformed YAML before any write).
        const flat = token.meta?.data ?? loadFlatYaml(token.content)
        if (flat === null) throw new Error('Invalid front matter YAML')
        const data = flat as Record<string, a>
        return [
          {
            type: 'pageMeta',
            // `id` is the page id: present only when the markdown names it. Document
            // parses (incremental ids) give front matter a local identity, as they do qids.
            ...(options.incrementalIds && { id: options.nextBlockId() }),
            ...data,
          } as PageMeta,
          index + 1,
        ]
      }
      return [null, index + 1]
    }

    case 'math_block': {
      // Block math: $$...$$
      return [
        {
          id: options.nextBlockId(),
          type: 'pre',
          latex: true,
          children: [
            {
              id: options.nextBlockId(),
              type: 'text',
              text: token.content.trim(),
            },
          ],
        },
        index + 1,
      ]
    }

    default:
      return [null, index + 1]
  }
}


/** Each answer paragraph is one alternative, regardless of inline formatting. */
function shortAnswerText(children: Blocks): string[] {
  if (children.length === 0) return ['']
  return children.some(child => child.type === 'p')
    ? children.map(child => comparableText([child]))
    : [comparableText(children).trim()]
}

function addPromptMetadata(
  target: Record<string, unknown>,
  attrs: Record<string, string>,
) {
  Object.assign(target, readPromptMetadata(attrs))
}

function readPromptMetadata(attrs: Record<string, string>) {
  return {
    ...(readOptionalDirectiveFlag(attrs, 'practice') ? { practice: true } : {}),
    ...(attrs.type ? { promptType: attrs.type } : {}),
    ...(isPromptLevel(attrs.importance)
      ? { importance: attrs.importance }
      : {}),
    ...(isPromptLevel(attrs.difficulty)
      ? { difficulty: attrs.difficulty }
      : {}),
  }
}

function readDirectiveFlag(
  attrs: Record<string, unknown>,
  name: string,
): boolean {
  return attrs[name] === true || attrs[name] === 'true' || hasClass(attrs, name)
}

function readOptionalDirectiveFlag(
  attrs: Record<string, unknown>,
  name: string,
): boolean | undefined {
  if (
    (attrs[name] === null || attrs[name] === undefined) &&
    !hasClass(attrs, name)
  )
    return undefined
  return readDirectiveFlag(attrs, name)
}

function hasClass(attrs: Record<string, unknown>, className: string): boolean {
  return (
    typeof attrs.class === 'string' &&
    attrs.class.split(/\s+/).includes(className)
  )
}

function readCriteria(value: string | undefined): string[] {
  if (!value) return []
  const parsed: unknown = JSON.parse(value.replaceAll("'", '"'))
  if (!Array.isArray(parsed)) return []
  return parsed.filter((item): item is string => typeof item === 'string')
}

/**
 * Parses a single-quoted-JSON directive attribute (e.g. answer="['a','b']")
 * without throwing. Malformed authoring input (missing attribute, invalid
 * JSON) yields `undefined` instead of crashing the whole parse.
 */
function readJsonAttr(value: string | undefined): unknown {
  if (!value) return undefined
  try {
    return JSON.parse(value.replaceAll("'", '"'))
  } catch {
    return undefined
  }
}

/**
 * Parses an `opts="['a','b']"` attribute into option blocks. `opts` is
 * `Blocks[]`, so each option's markdown is parsed rather than kept as a raw
 * string — otherwise inline formatting in an option would never render.
 * Returns `undefined` for malformed input so callers can drop the block.
 */
function optsToBlocks(
  value: string | undefined,
  options: Options,
): Blocks[] | undefined {
  const parsed = readJsonAttr(value)
  if (!Array.isArray(parsed) || parsed.length === 0) return undefined
  if (!parsed.every((item): item is string => typeof item === 'string'))
    return undefined
  return parsed.map((opt) => mdToInlineEditorBlocks(opt, options))
}

function isPromptLevel(
  value: string | undefined,
): value is 'low' | 'medium' | 'high' {
  return value === 'low' || value === 'medium' || value === 'high'
}

/**
 * Apply a format bit to an array of blocks.
 * chars blocks: merge the bit into their format field.
 * non-chars blocks (a, em, cloze, etc.): passed through unchanged — they carry
 * their own semantics and don't participate in the format bitmask.
 */
function applyFormatToBlocks(
  childBlocks: Blocks,
  bit: FormatFlag,
  options: Options,
): Blocks {
  if (options.plainTextBlocks) return childBlocks

  return childBlocks.map((b) => {
    if (b.type === 'chars') {
      const f = (b.format ?? 0) | bit
      return { ...b, format: f || undefined }
    }
    if (b.type === 'text') {
      // text blocks → upgrade to chars with format bit
      return {
        id: b.id,
        type: 'chars' as const,
        text: textToBlockLetters(b.text),
        format: bit,
      }
    }
    return b
  })
}

function inlineTokensToBlocks(tokens: Token[], options: ParseOptions): Blocks {
  const blocks: Blocks = []
  let i = 0

  while (i < tokens.length) {
    const token = tokens[i]

    if (token.type === 'diff_inline') {
      const metadataState = criticReviewState(tokens[i + 1])
      const review = criticDiffToReview(token, metadataState, options)
      if (review) blocks.push(review)
      i += metadataState ? 2 : 1
      continue
    }

    // Handle open/close pairs (strong, em, s, etc.)
    if (token.type.endsWith('_open')) {
      const baseType = token.type.replace('_open', '')
      const closeType = `${baseType}_close`

      // Find matching close token
      let depth = 1
      let closeIndex = i + 1
      while (closeIndex < tokens.length && depth > 0) {
        if (tokens[closeIndex].type === token.type) depth++
        if (tokens[closeIndex].type === closeType) depth--
        if (depth === 0) break
        closeIndex++
      }

      // Extract children between open and close
      const childTokens = tokens.slice(i + 1, closeIndex)
      const children = inlineTokensToBlocks(childTokens, options)

      // Create block based on type
      let block: Block | null = null
      switch (baseType) {
        case 'strong':
          // Merge BOLD into children chars blocks; pass-through non-chars blocks
          for (const child of applyFormatToBlocks(
            children,
            FormatFlag.BOLD,
            options,
          )) {
            blocks.push(child)
          }
          i = closeIndex + 1
          continue
        case 'em':
          for (const child of applyFormatToBlocks(
            children,
            FormatFlag.ITALIC,
            options,
          )) {
            blocks.push(child)
          }
          i = closeIndex + 1
          continue
        case 's':
          for (const child of applyFormatToBlocks(
            children,
            FormatFlag.STRIKE,
            options,
          )) {
            blocks.push(child)
          }
          i = closeIndex + 1
          continue
        case 'mark': {
          const classAttr = token.attrGet('class')
          let color = 'default'
          if (classAttr?.startsWith('mark-')) {
            color = classAttr.slice(5) // Remove 'mark-' prefix
          }
          block = { id: options.nextBlockId(), type: 'em', color, children }
          break
        }
        case 'wikilink': {
          const href = token.attrGet('href') ?? ''
          const rawTarget =
            typeof token.meta?.label === 'string' ? token.meta.label : href
          block = {
            id: options.nextBlockId(),
            type: 'pageLink',
            ...parsePageLinkTarget(normalizeWikilinkTarget({ href, rawTarget })),
          }
          break
        }
        case 'mention': {
          const username = token.meta?.username ?? token.attrGet('href') ?? ''
          block = {
            id: options.nextBlockId(),
            type: 'mention',
            subtype: 'user',
            uid: username,
            $at: true,
          }
          break
        }
        case 'link': {
          const href = token.attrGet('href') ?? ''
          const mediaType = token.attrGet('type')

          // Media types: [caption](src){type="video|audio|file"}
          if (mediaType === 'video') {
            const width = token.attrGet('width')
            const autoplay = token.attrGet('autoplay') !== null
            // The caption attribute; a stored link text is re-read as the caption's Markdown, as it always was.
            const captionMd = token.attrGet('caption') ?? comparableText(children)
            const caption = captionMd
              ? (token.attrGet('captionInline') ? mdToInlineEditorBlocks(captionMd, options) : mdToEditorBlocks(captionMd, options))
              : undefined
            block = {
              id: options.nextBlockId(),
              type: 'video',
              src: href,
              ...widthField(width),
              ...(autoplay ? { autoplay } : {}),
              ...(caption ? { caption } : {}),
            }
            break
          }
          if (mediaType === 'audio') {
            const autoplay = token.attrGet('autoplay') !== null
            block = {
              id: options.nextBlockId(),
              type: 'audio',
              src: href,
              ...(autoplay ? { autoplay } : {}),
            }
            break
          }
          if (mediaType === 'file') {
            const size = token.attrGet('size')
            block = {
              id: options.nextBlockId(),
              type: 'file',
              src: href,
              name: comparableText(children),
              size: size ? Number(size) : 0,
            }
            break
          }

          // Check for page reference
          if (mediaType !== 'link' && comparableText(children) === href && !href.startsWith('http')) {
            block = { id: options.nextBlockId(), type: 'page', pageId: href }
            break
          }

          // Check for pages reference
          if (mediaType !== 'link' && comparableText(children) === '---') {
            block = {
              id: options.nextBlockId(),
              type: 'pages',
              ids: href ? href.split(',').map((s) => s.trim()) : [],
            }
            break
          }

          block = { id: options.nextBlockId(), type: 'a', href, children }
          break
        }
      }

      if (block) {
        blocks.push(block)
      }

      i = closeIndex + 1
      continue
    }

    const block = inlineTokenToBlock(token, options)
    if (block) {
      blocks.push(block)
    }
    i++
  }

  return blocks
}

function normalizeWikilinkTarget(args: {
  href: string
  rawTarget: string
}): string {
  const trimmed = args.rawTarget.trim()
  if (trimmed.startsWith('space:')) return trimmed
  const suffix = trimmed.split('-').at(-1)
  if (suffix && suffix !== trimmed && /^[0-9A-Z_a-z]{6,}$/.test(suffix)) {
    return suffix
  }
  if (/^[0-9A-Za-z_-]{6,}$/.test(trimmed)) return trimmed
  return args.href
}

function parseCommentThreads(
  children: Blocks,
  rawContent: unknown,
): CommentThread[] {
  const source = (
    typeof rawContent === 'string'
      ? rawContent
      : blocksToMd(children, { plainText: true })
  ).trim()
  let value: unknown
  try {
    value = JSON.parse(source)
  } catch {
    throw new Error('comments registry must contain valid JSON')
  }
  if (!Array.isArray(value)) {
    throw new TypeError('comments registry must contain a JSON array')
  }
  return value.map((thread, index) => parseCommentThread(thread, index))
}

function parseCommentThread(value: unknown, index: number): CommentThread {
  if (!isExactRecord(value, ['threadId', 'status', 'comments']) && !isExactRecord(value, ['threadId', 'status', 'scope', 'comments'])) {
    throw new Error(`comments registry thread ${index} has invalid fields`)
  }
  if (typeof value.threadId !== 'string' || value.threadId.trim() === '') {
    throw new Error(`comments registry thread ${index} requires id`)
  }
  const threadId = value.threadId
  if (value.status !== 'open' && value.status !== 'resolved') {
    throw new Error(`comments registry thread ${threadId} has invalid status`)
  }
  if (value.scope !== undefined && value.scope !== 'page') {
    throw new Error(`comments registry thread ${threadId} has invalid scope`)
  }
  if (!Array.isArray(value.comments)) {
    throw new TypeError(
      `comments registry thread ${threadId} requires comments`,
    )
  }
  return {
    threadId,
    status: value.status,
    ...(value.scope === 'page' ? { scope: 'page' as const } : {}),
    comments: value.comments.map((comment, commentIndex) =>
      parseCommentMessage(comment, threadId, commentIndex),
    ),
  }
}

function parseCommentMessage(
  value: unknown,
  threadId: string,
  index: number,
): CommentMessage {
  if (!isExactRecord(value, ['id', 'authorId', 'createdAt', 'body']) && !isExactRecord(value, ['id', 'authorId', 'createdAt', 'editedAt', 'body'])) {
    throw new Error(
      `comments registry thread ${threadId} comment ${index} has invalid fields`,
    )
  }
  if (typeof value.id !== 'string' || value.id.trim() === '') {
    throw new Error(`comments registry thread ${threadId} comment requires id`)
  }
  if (typeof value.authorId !== 'string' || value.authorId.trim() === '') {
    throw new Error(`comment ${value.id} requires authorId`)
  }
  if (
    typeof value.createdAt !== 'number' ||
    !Number.isSafeInteger(value.createdAt) ||
    value.createdAt < 0
  ) {
    throw new Error(`comment ${value.id} requires Unix createdAt milliseconds`)
  }
  if (typeof value.body !== 'string') {
    throw new TypeError(`comment ${value.id} requires Markdown body`)
  }
  if (value.editedAt !== undefined && (typeof value.editedAt !== 'number' || !Number.isSafeInteger(value.editedAt) || value.editedAt < 0)) {
    throw new TypeError(`comment ${value.id} requires Unix editedAt milliseconds`)
  }
  return {
    id: value.id,
    authorId: value.authorId,
    createdAt: value.createdAt,
    ...(value.editedAt === undefined ? {} : { editedAt: value.editedAt }),
    body: value.body,
  }
}

function isExactRecord(
  value: unknown,
  fields: readonly string[],
): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false
  }
  const keys = Object.keys(value)
  return (
    keys.length === fields.length && keys.every((key) => fields.includes(key))
  )
}

function criticReviewState(
  token: Token | undefined,
): 'suggested' | 'approved' | null {
  if (token?.type !== 'critic_comment') return null
  if (token.content === 'review:approved') return 'approved' as const
  if (token.content === 'review:suggested') return 'suggested' as const
  return null
}

function reviewFromDirective(
  attributes: Record<string, string>,
  children: Blocks,
  options: ParseOptions,
): Extract<Block, { type: 'review' }> {
  const change = attributes.change
  const state = attributes.state
  if (change !== 'add' && change !== 'delete' && change !== 'update') {
    throw new Error('review requires add, delete, or update change')
  }
  if (state !== 'suggested' && state !== 'approved') {
    throw new Error('review requires suggested or approved state')
  }
  return {
    id: readBlockId(attributes, options),
    type: 'review',
    change,
    state,
    display: 'block',
    children,
  }
}

function criticDiffToReview(
  token: Token,
  state: 'suggested' | 'approved' | null,
  options: ParseOptions,
): Extract<Block, { type: 'review' }> | null {
  const kind = token.meta?.kind
  if (kind === 'add' || kind === 'delete') {
    return {
      id: options.nextBlockId(),
      type: 'review',
      change: kind,
      state: state ?? 'suggested',
      display: 'inline',
      children: [
        {
          id: options.nextBlockId(),
          type: 'diff',
          kind,
          children: mdToInlineEditorBlocks(token.content, options),
        },
      ],
    }
  }
  if (
    kind !== 'update' ||
    typeof token.meta?.old !== 'string' ||
    typeof token.meta.new !== 'string'
  ) {
    return null
  }
  return {
    id: options.nextBlockId(),
    type: 'review',
    change: 'update',
    state: state ?? 'suggested',
    display: 'inline',
    children: [
      {
        id: options.nextBlockId(),
        type: 'diff',
        kind: 'delete',
        children: mdToInlineEditorBlocks(token.meta.old, options),
      },
      {
        id: options.nextBlockId(),
        type: 'diff',
        kind: 'add',
        children: mdToInlineEditorBlocks(token.meta.new, options),
      },
    ],
  }
}

function inlineTokenToBlock(token: Token, options: ParseOptions): Block | null {
  switch (token.type) {
    case 'diff_inline': {
      const kind = token.meta?.kind
      if (kind !== 'add' && kind !== 'delete') return null
      return {
        id: options.nextBlockId(),
        type: 'diff',
        kind,
        children: mdToInlineEditorBlocks(token.content, options),
      }
    }
    case 'critic_comment': {
      return {
        id: options.nextBlockId(),
        type: 'criticComment',
        body: token.content,
      }
    }
    case 'text': {
      // Skip empty text nodes
      if (!token.content) return null

      if (options.withChars) {
        return {
          id: options.nextBlockId(),
          type: 'chars',
          text: textToBlockLetters(token.content),
        }
      }
      return {
        id: options.nextBlockId(),
        type: 'text',
        text: token.content,
      }
    }

    case 'hardbreak': {
      return {
        id: options.nextBlockId(),
        type: 'br',
      }
    }

    case 'softbreak': {
      return {
        id: options.nextBlockId(),
        type: 'text',
        text: '\n',
      }
    }

    case 'strong_open': {
      // Note: this is never called because inline parsing works differently
      // The children are already parsed, so we handle it in the token directly
      return null
    }

    case 'code_inline': {
      // Plain inline code → format bitmask, no wrapper block.
      // text mode: text block with escaped=true (serializer wraps in backticks).
      // chars mode: chars block with format & CODE.
      if (options.withChars) {
        return {
          id: options.nextBlockId(),
          type: 'chars',
          text: textToBlockLetters(token.content),
          format: FormatFlag.CODE,
        }
      }
      return {
        id: options.nextBlockId(),
        type: 'text',
        text: token.content,
        escaped: true,
      }
    }

    case 'math_inline': {
      // Inline math: $...$
      return {
        id: options.nextBlockId(),
        type: 'code',
        latex: true,
        children: [
          {
            id: options.nextBlockId(),
            type: 'text',
            text: token.content.trim(),
          },
        ],
      }
    }

    case 'directive_inline': {
      const name = token.meta?.name
      const content = token.meta?.content ?? ''
      const attributes = token.meta?.attributes ?? {}

      // Parse content as inline markdown
      const children = content ? mdToInlineEditorBlocks(content, options) : []

      // Map directive names to block types
      switch (name) {
        case 'escaped': {
          const text = blocksToMd(children, { plainText: true, trim: false })
          const format = Number(attributes.format ?? 0)
          return options.withChars || format
            ? { id: options.nextBlockId(), type: 'chars', text: textToBlockLetters(text), format, escaped: true }
            : { id: options.nextBlockId(), type: 'text', text, escaped: true }
        }
        case 'br': return { id: options.nextBlockId(), type: 'br' }
        case 'latex': return { id: readBlockId(attributes, options), type: 'code', latex: true, children }
        case 'diff': return { id: options.nextBlockId(), type: 'diff', kind: attributes.kind, children }
        case 'review': return { id: options.nextBlockId(), type: 'review', display: attributes.display === 'block' ? 'block' : 'inline', change: attributes.change, state: attributes.state, children }
        case 'u': {
          // Underline → format bitmask on children chars, no wrapper block
          const formatted = applyFormatToBlocks(
            children,
            FormatFlag.UNDERLINE,
            options,
          )
          return formatted[0] ?? null
        }
        case 'comment': {
          const commentId = attributes.commentId ?? content.trim()
          if (!commentId) throw new Error('comment requires a thread id')
          return {
            id: options.nextBlockId(),
            type: 'comment',
            commentId,
            children: attributes.commentId ? children : [],
          }
        }
        case 'mention': {
          const mentionContent = content
          if (mentionContent.startsWith('u-')) {
            const uid = mentionContent.slice(2)
            return {
              id: options.nextBlockId(),
              type: 'mention',
              subtype: 'user',
              uid,
            }
          }
          if (mentionContent.startsWith('p-')) {
            return {
              id: options.nextBlockId(),
              type: 'mention',
              subtype: 'page',
              uid: mentionContent.slice(2),
            }
          }
          if (mentionContent.startsWith('d-')) {
            const dateStr = mentionContent.slice(2)
            const date = Math.trunc(Number(dateStr))
            return {
              id: options.nextBlockId(),
              type: 'mention',
              subtype: 'date',
              date,
            }
          }
          return null
        }
        case 'cloze': {
          const qid = attributes.qid ?? (options.incrementalIds ? options.nextBlockId() : ulid())
          const fractionalIndex = readFractionalIndex(attributes, options)
          return {
            id: readBlockId(attributes, options),
            type: 'cloze',
            qid,
            fractionalIndex,
            answerRegexp: attributes.answerRegexp ?? attributes.regexp,
            ...(attributes.answerAudioSrc
              ? {
                  answerAudio: {
                    src: attributes.answerAudioSrc,
                    ...(attributes.answerAudioFile
                      ? { $file: attributes.answerAudioFile }
                      : {}),
                  },
                }
              : {}),
            ...readPromptMetadata(attributes),
            tip: attributes.tip,
            ...(readOptionalDirectiveFlag(attributes, 'practice') ? { practice: true } : {}),
            $showAnswer: readOptionalDirectiveFlag(attributes, 'showAnswer'),
            children,
          }
        }
        case 'explanation': {
          return {
            id: readBlockId(attributes, options),
            type: 'explanation',
            children,
          } as any
        }
        case 'answer': {
          return {
            id: readBlockId(attributes, options),
            type: 'answer',
            children,
          } as any
        }
        case 'opt': {
          return {
            id: readBlockId(attributes, options),
            type: 'opt',
            children,
            correct: readDirectiveFlag(attributes, 'correct'),
          } as any
        }
        case 'l': {
          const lang = attributes.lang
          if (!lang) throw new Error('l requires lang')
          return { id: options.nextBlockId(), type: 'l', lang, children }
        }
        case 'mark': {
          const color = attributes.color ?? 'default'
          return { id: options.nextBlockId(), type: 'em', color, children }
        }
        case 'sa':
        case 'sc':
        case 'mc': {
          const qid: string = attributes.qid ?? (options.incrementalIds ? options.nextBlockId() : ulid())
          const fractionalIndex = readFractionalIndex(attributes, options)
          const subtype = name as 'sa' | 'sc' | 'mc'

          let answer: number | number[] | string[]
          let opts: Blocks[] | undefined
          let explanation: Blocks | undefined

          if (attributes.explanation)
            explanation = mdToEditorBlocks(attributes.explanation, options)

          const strict = subtype === 'sa' ? readOptionalDirectiveFlag(attributes, 'strict') : undefined
          const answerRegexp = subtype === 'sa' ? (attributes.answerRegexp ?? attributes.regexp) : undefined

          if (subtype === 'sa') {
            const parsedAnswer = readJsonAttr(attributes.answer)
            // A short-answer question with no accepted answers can never be
            // graded correct, so it's not a meaningful block - drop it
            // rather than emit a half-built question (matches the 'mention'
            // case above, which also returns null for unparseable content).
            if (
              !Array.isArray(parsedAnswer) ||
              parsedAnswer.some((item) => typeof item !== 'string') ||
              parsedAnswer.length === 0
            )
              return null
            answer = parsedAnswer
          } else if (subtype === 'sc') {
            const parsedOpts = optsToBlocks(attributes.opts, options)
            const parsedAnswer = Math.trunc(Number(attributes.answer))
            // sc/mc questions are unrenderable without their option list, and
            // an out-of-range/non-numeric answer can't select any option -
            // drop the block instead of rendering broken UI.
            if (!parsedOpts || Number.isNaN(parsedAnswer)) return null
            opts = parsedOpts
            answer = parsedAnswer
          } else {
            const parsedOpts = optsToBlocks(attributes.opts, options)
            const parsedAnswer = readJsonAttr(attributes.answer)
            if (
              !parsedOpts ||
              !Array.isArray(parsedAnswer) ||
              parsedAnswer.some((item) => typeof item !== 'number')
            )
              return null
            opts = parsedOpts
            answer = parsedAnswer
          }

          return {
            id: readBlockId(attributes, options),
            type: 'question',
            qid,
            fractionalIndex,
            subtype,
            inline: true,
            ...readPromptMetadata(attributes),
            answer,
            ...(opts !== undefined && { opts }),
            ...(explanation !== undefined && { explanation }),
            ...(strict !== undefined && { strict }),
            ...(answerRegexp && { answerRegexp }),
          }
        }
        default:
          return null
      }
    }

    case 'image': {
      // Handle images with attributes: ![alt](url){attrs}
      // If has type attribute, it's a preview, otherwise it's an img
      const attrs = token.attrs ?? []
      const typeAttr = attrs.find(([k]) => k === 'type')
      const result: any = {
        id: options.nextBlockId(),
        type: typeAttr ? 'preview' : 'img',
        ...(token.content && { alt: token.content }),
      }

      // Extract attributes
      for (const [key, value] of attrs) {
        // A preview has no alt text; the image syntax always spells an empty one
        if (key === 'captionInline') continue
        if (key === 'alt' && result.type === 'preview' && !value) continue
        if (key === 'src' || key === 'alt' || key === 'title') {
          // These are standard image attributes, store them directly
          result[key] = value
        } else if (key === 'type') {
          // For preview, type becomes subtype
          result.subtype = value
        } else if (key === 'caption') {
          result.caption = token.attrGet('captionInline') ? mdToInlineEditorBlocks(value, options) : mdToEditorBlocks(value, options)
        } else if (key === 'children') {
          result.children = value ? mdToInlineEditorBlocks(value, options) : []
        } else if (key === 'size') {
          result[key] = Number(value)
        } else if (key === 'width') {
          Object.assign(result, widthField(value))
        } else {
          result[key] = value
        }
      }

      if (result.type === 'img' && !result.alt) {
        result.alt =
          token.children?.map((child) => child.content).join('') ?? ''
      }

      return result
    }

    case 'link_open': {
      const _href = token.attrGet('href') ?? ''
      // We need to look ahead to get the text content
      // This is handled at a higher level
      return null
    }

    default: {
      // Handle token with children (strong, em, etc)
      if (token.children && token.children.length > 0) {
        const children = inlineTokensToBlocks(token.children, options)

        switch (token.type) {
          case 'strong': {
            const formatted = applyFormatToBlocks(
              children,
              FormatFlag.BOLD,
              options,
            )
            return formatted[0] ?? null
          }
          case 'em': {
            const formatted = applyFormatToBlocks(
              children,
              FormatFlag.ITALIC,
              options,
            )
            return formatted[0] ?? null
          }
          case 's': {
            const formatted = applyFormatToBlocks(
              children,
              FormatFlag.STRIKE,
              options,
            )
            return formatted[0] ?? null
          }
          case 'mark': {
            const className = token.meta?.className
            const color =
              className === 'default'
                ? 'default'
                : (className?.[0] ?? 'default')
            return { id: options.nextBlockId(), type: 'em', color, children }
          }
          case 'link': {
            const href = token.attrGet('href') ?? ''
            const mediaType = token.attrGet('type')
            const textToken = children[0]
            const linkText = textToken?.type === 'text' ? textToken.text : null

            // Check for media types: [caption](src){type="video|audio|file"}
            if (mediaType === 'video') {
              const width = token.attrGet('width')
              const autoplay = token.attrGet('autoplay') !== null
              const captionMd = token.attrGet('caption') ?? linkText
              const caption = captionMd
                ? (token.attrGet('captionInline') ? mdToInlineEditorBlocks(captionMd, options) : mdToEditorBlocks(captionMd, options))
                : undefined
              return {
                id: options.nextBlockId(),
                type: 'video',
                src: href,
                ...widthField(width),
                ...(autoplay ? { autoplay } : {}),
                ...(caption ? { caption } : {}),
              }
            }

            if (mediaType === 'audio') {
              const autoplay = token.attrGet('autoplay') !== null
              return {
                id: options.nextBlockId(),
                type: 'audio',
                src: href,
                ...(autoplay ? { autoplay } : {}),
              }
            }

            if (mediaType === 'file') {
              const size = token.attrGet('size')
              return {
                id: options.nextBlockId(),
                type: 'file',
                src: href,
                name: linkText ?? '',
                size: size ? Number(size) : 0,
              }
            }

            // Check for page reference
            if (mediaType !== 'link' && linkText === href && !href.startsWith('http')) {
              return { id: options.nextBlockId(), type: 'page', pageId: href }
            }

            // Check for pages reference
            if (mediaType !== 'link' && linkText === '---') {
              return {
                id: options.nextBlockId(),
                type: 'pages',
                ids: href ? href.split(',').map((s) => s.trim()) : [],
              }
            }

            return { id: options.nextBlockId(), type: 'a', href, children }
          }
        }
      }

      return null
    }
  }
}

function diffCodeLinesToBlocks(content: string, options: ParseOptions): Blocks {
  if (content === '') return []
  return content.split('\n').map((line) => {
    const first = line[0]
    const kind = first === '+' ? 'add' : first === '-' ? 'delete' : 'context'
    const text =
      first === '+' || first === '-' || first === ' ' ? line.slice(1) : line
    return {
      id: options.nextBlockId(),
      type: 'diff' as const,
      kind,
      children: text
        ? [{ id: options.nextBlockId(), type: 'text' as const, text }]
        : [],
    }
  })
}


/** Short-answer strings are Markdown source (README "short answers"); grading and the training reveal use this text. */
export function answerPlainText(markdown: string): string {
  return _answerText(mdToBlocks(markdown))
}

function _answerText(blocks: Blocks): string {
  return blocks.map((block) => block.type === 'text' ? block.text
    : block.type === 'chars' ? block.text.map((part) => part.text).join('')
      : block.type === 'br' ? '\n'
        : 'children' in block ? _answerText(block.children as Blocks) : '').join('')
}

/**
 * A run's Markdown as the reader has always compared it (sa answers, a file name, a link text naming its page or
 * `---`): text escaped the way the writer escaped it before context-aware escapes, so stored documents read the same.
 */
function comparableText(children: Blocks): string {
  return blocksToMd(children, { legacyTextEscape: true })
}

function hasBlankBetweenChildren(tokens: Token[], start: number, end: number): boolean {
  let previousEnd: number | undefined
  const level = tokens[start].level + 1
  for (let index = start + 1; index < end; index += 1) {
    const token = tokens[index]
    if (token.level !== level || !token.map || token.nesting === -1) continue
    if (previousEnd !== undefined && token.map[0] > previousEnd) return true
    previousEnd = token.map[1]
    // Container maps can include blank lines after their last actual content.
    if (token.nesting === 1) {
      for (let child = index + 1; child < end && tokens[child].level > level; child += 1) {
        const map = tokens[child].map
        if (map) previousEnd = map[1]
      }
    }
  }
  return false
}

function normalizeEditorText(input: Blocks, options: ParseOptions): Blocks {
  if (!options.withChars) return input
  const visit = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map((item: unknown) => visit(item))
    if (!value || typeof value !== 'object') return value
    if ('type' in value && value.type === 'text' && 'text' in value && typeof value.text === 'string') {
      return { ...value, type: 'chars', text: [{ id: '', text: value.text }] }
    }
    return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, visit(child)]))
  }
  return visit(input) as Blocks
}

function parsePageLinkTarget(target: string): { pageId: string; spaceId?: string } {
  const qualified = /^space:([^/]+)\/(.+)$/.exec(target)
  return qualified ? { spaceId: decodeURIComponent(qualified[1]), pageId: decodeURIComponent(qualified[2]) } : { pageId: target }
}
